import { useCallback, useEffect, useRef, useState } from 'react';
import useBackofficeAxios from '../../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../../hooks/useBackofficeOwnerAuth';
import { useAppSelector } from '../../../redux/helper';
import { getStockValueInformation } from '../../../api/backofficeProducts';
import { toFiniteNumber } from '../../../utils/backofficeProductView';
import {
  DEFAULT_INVENTORY_FILTERS,
  buildStockValueQuery,
  type InventoryFilters,
} from '../../../utils/inventoryFilters';

export type StockValueCategory = {
  /** Empty for products with no category. */
  category: string;
  products: number;
  inStock: number;
  outOfStock: number;
  noBuyingPrice: number;
  buyingValue: number;
  sellingValue: number;
  potentialProfit: number;
};

export type StockValueSummary = {
  buyingValue: number;
  sellingValue: number;
  potentialProfit: number;
  productsTotal: number;
  productsInStock: number;
  productsOutOfStock: number;
  noBuyingPriceCount: number;
  byCategory: StockValueCategory[];
};

/**
 * Reads `/bp_get_stock_value_information`. Null for anything but an explicit
 * success, so callers keep their "—" placeholders.
 */
export const parseStockValueSummary = (res: any): StockValueSummary | null => {
  if (res?.status !== 200) return null;
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  if (body.status != null && Number(body.status) !== 200) return null;
  const data = body.data;
  if (!data || typeof data !== 'object') return null;
  const num = (value: unknown) => toFiniteNumber(value) ?? 0;
  const byCategory = Array.isArray(data.byCategory)
    ? data.byCategory.map((row: any) => ({
        category: String(row?.category ?? ''),
        products: num(row?.products),
        inStock: num(row?.inStock),
        outOfStock: num(row?.outOfStock),
        noBuyingPrice: num(row?.noBuyingPrice),
        buyingValue: num(row?.buyingValue),
        sellingValue: num(row?.sellingValue),
        potentialProfit: num(row?.potentialProfit),
      }))
    : [];
  return {
    buyingValue: num(data.buyingValue),
    sellingValue: num(data.sellingValue),
    potentialProfit: num(data.potentialProfit),
    productsTotal: num(data.productsTotal),
    productsInStock: num(data.productsInStock),
    productsOutOfStock: num(data.productsOutOfStock),
    noBuyingPriceCount: num(data.noBuyingPriceCount),
    byCategory,
  };
};

export type StockValueState = {
  summary: StockValueSummary | null;
  loading: boolean;
  failed: boolean;
  reload: () => Promise<void>;
};

/**
 * Stock value for the location in the header: the whole store, or the
 * products `filters` select. Refetches when either changes.
 */
export const useStockValue = (
  filters: InventoryFilters = DEFAULT_INVENTORY_FILTERS,
): StockValueState => {
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const locationVersion = useAppSelector(s => s.location.locationVersion);
  const [summary, setSummary] = useState<StockValueSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef(0);
  const query = buildStockValueQuery(filters);

  const reload = useCallback(async () => {
    if (!owner?.accessToken) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const res = await getStockValueInformation(
        axiosBackoffice as any,
        { accessToken: owner.accessToken },
        query,
      );
      if (requestId !== requestRef.current) return;
      const parsed = parseStockValueSummary(res);
      setSummary(parsed);
      setFailed(parsed == null);
    } catch {
      if (requestId !== requestRef.current) return;
      setSummary(null);
      setFailed(true);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [axiosBackoffice, owner?.accessToken, query]);

  // A new selection or location shows "—" until its own value arrives, never
  // the previous one under the new title
  useEffect(() => {
    setSummary(null);
    setFailed(false);
    void reload();
  }, [reload, locationVersion]);

  return { summary, loading, failed, reload };
};
